import { BasicButton, Stack } from "@certn/cascadia";
import { Space, FieldErrors } from "@certn/cascadia-react";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { createContext, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { BaseLayout } from "./BaseLayout";
import { BaseLayoutContent } from "./BaseLayoutContent";
import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import {
  CascadiaLayoutProps,
  withCascadiaLayoutProps,
} from "../wrappers/withCascadiaLayoutProps";

interface Context {
  isCollapsed: boolean;
  setIsCollapsed?: (collapsed: boolean) => void;
  /**
   * The most recent value (yes/no) of a `QuestionControl` within this card. I'm assuming there'll be at most 1 per card.
   */
  lastQuestionControlValue: boolean | undefined;
  setLastQuestionControlValue?: (value: boolean | undefined) => void;
}

export const CollapsibleCardContext = createContext<Context>({
  isCollapsed: false,
  lastQuestionControlValue: undefined,
});

function CollapsibleCardLayout(props: CascadiaLayoutProps) {
  const { isSummaryForPath } = useContext(CascadiaJsonFormContext);
  const isSummary = isSummaryForPath?.(props.path);

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [lastQuestionControlValue, setLastQuestionControlValue] =
    useState<boolean>();
  const { t } = useTranslation();

  return (
    <CollapsibleCardContext.Provider
      value={{
        isCollapsed,
        setIsCollapsed: (collapse) => {
          setIsCollapsed(collapse);
          props.setTouched();
        },
        lastQuestionControlValue,
        setLastQuestionControlValue,
      }}
    >
      <BaseLayout
        {...props}
        direction="column"
        testID={`${slugify(props.label?.toLowerCase())}-collapsible-card-layout`}
        className={css(
          styles.layout,
          !isSummary && styles.card,
          isSummary && {
            display: "block",
          },
          props.invalid && styles.error,
          {
            ":has(.has-errors)": styles.error,
          }
        )}
      >
        <div>
          <Stack
            className={css(
              isCollapsed &&
                !isSummary && {
                  "> *:not(:first-child)": css({
                    display: "none",
                  }),
                },
              props.invalid &&
                css({
                  ".form-feedback": { display: "none" },
                })
            )}
            {...(isSummary ? { gap: "space-0" } : {})}
          >
            <BaseLayoutContent />
          </Stack>
        </div>

        {props.touched && <FieldErrors>{props.errors}</FieldErrors>}

        {!isSummary && !isCollapsed && (
          <Stack orientation="horizontal">
            <Space />
            <BasicButton
              testID="close-button"
              onPress={() => {
                setIsCollapsed(true);
              }}
            >
              {t("Close")}
            </BasicButton>
          </Stack>
        )}
      </BaseLayout>
    </CollapsibleCardContext.Provider>
  );
}

const styles = {
  layout: css({
    display: "flex",
    flexDirection: "column",
  }),
  card: css({
    border: "1px solid var(--border-base)",
    borderRadius: "var(--border-radius-3)",
    padding: "var(--space-5)",
    gap: "var(--space-4)",
  }),
  error: css({
    border: "1px solid var(--border-danger-input)",
    borderRadius: "var(--border-radius-3)",
  }),
};

CollapsibleCardLayout.displayName = "CollapsibleCardLayout";

export const CollapsibleCardLayoutRenderer = withCascadiaLayoutProps(
  CollapsibleCardLayout
);
